import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ChatContextType } from '@trinity/chat';
import { ChatPageContextDataContext } from '@trinity/chat-components/components/Chat/context.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import type { SegmentationRunSummary } from '../Segmentation/api/client';
import { SegmentationRunSelector } from '../Segmentation/components/SegmentationRunSelector';

const MESSAGE_GENERATION_AGENT_IDENTIFIER = 'message-generation-agent';

type MessageGenerationWrapperProps = {
  children: React.ReactNode;
};

/**
 * Wrapper that shows run selector when message-generation-agent is selected
 * but no sessionId is present in the context.
 */
export function MessageGenerationWrapper({ children }: MessageGenerationWrapperProps) {
  const contextData = useContext(ChatPageContextDataContext);
  const apiClient = useChatApiClient();
  const navigate = useNavigate();

  const [prefetchedRuns, setPrefetchedRuns] = useState<SegmentationRunSummary[] | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  // Check if we're in message-generation-agent mode
  const isMessageGenerationAgent = useMemo(() => {
    if (!contextData || contextData.type !== ChatContextType.Agent) return false;
    return contextData.data.agent.identifier.startsWith(MESSAGE_GENERATION_AGENT_IDENTIFIER);
  }, [contextData]);

  // Check if we have sessionId in metadata
  const hasSessionId = useMemo(() => {
    if (!contextData || contextData.type !== ChatContextType.Agent) return false;
    return !!contextData.metadata?.messageGenSessionId || !!contextData.metadata?.sessionId;
  }, [contextData]);

  const showRunSelector = isMessageGenerationAgent && !hasSessionId && !isCreatingSession;

  // Prefetch runs when message-generation-agent mounted without sessionId
  useEffect(() => {
    if (!showRunSelector) return;
    if (prefetchedRuns !== null) return; // Already fetched

    apiClient.authenticatedClient
      .get<{ runs: any[] }>('/segmentation-runs', {
        params: { status: 'complete', take: 5 },
      })
      .then((response) => {
        const filtered = response.data.runs.filter((r: any) => r.sessionId);
        setPrefetchedRuns(filtered);
      })
      .catch((error) => {
        console.error('[MessageGenerationWrapper] Failed to prefetch runs:', error);
        setPrefetchedRuns([]); // Set empty to prevent retry
      });
  }, [showRunSelector, prefetchedRuns, apiClient]);

  const handleSelectSegment = async (runId: number, sessionId: string, segmentIdentifier: string) => {
    setIsCreatingSession(true);

    try {
      const response = await apiClient.authenticatedClient.post<{
        sessionId: string;
        chatInstanceId: string;
      }>('/message-gen-sessions/start', {
        config: {
          segmentIdentifier,
          segmentationSessionId: sessionId,
          count: 5,
          category: 'Efficacy',
          targetAudience: 'HCPs',
        },
      });

      // Redirect to the new chat instance
      navigate(`/chat/${response.data.chatInstanceId}`);
    } catch (error) {
      console.error('[MessageGenerationWrapper] Failed to create message generation session:', error);
      setIsCreatingSession(false);
    }
  };

  const handleSelectRun = async (runId: number, sessionId: string) => {
    setIsCreatingSession(true);
    try {
      const response = await apiClient.authenticatedClient.post<{
        sessionId: string;
        chatInstanceId: string;
      }>('/message-gen-sessions/start', {
        config: {
          segmentationSessionId: sessionId,
          count: 5,
          category: 'Efficacy',
          targetAudience: 'HCPs',
        },
      });

      navigate(`/chat/${response.data.chatInstanceId}`);
    } catch (error) {
      console.error('[MessageGenerationWrapper] Failed to create whole-run message generation session:', error);
      setIsCreatingSession(false);
    }
  };

  if (showRunSelector) {
    return (
      <Root>
        <PageHeader>
          <StyledAutoAwesomeIcon />
          <PageHeaderTitle>Generate Messages</PageHeaderTitle>
        </PageHeader>
        <SegmentationRunSelector
          onSelectRun={handleSelectRun}
          onSelectSegment={handleSelectSegment}
          prefetchedRuns={prefetchedRuns}
        />
      </Root>
    );
  }

  return <>{children}</>;
}

const Root = styled.div({
  height: '100%',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
});

const StyledAutoAwesomeIcon = styled(AutoAwesomeIcon)(({ theme }) => ({
  fontSize: 20,
  color: theme.palette.primary.main,
}));

const PageHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 8px 0',
  marginBottom: '8px',
});

const PageHeaderTitle = styled.span({
  fontSize: 16,
  fontWeight: 600,
  color: '#1a1a1a',
  flex: 1,
});
